import Segment from 'offline/insights/segment'
import Util from 'offline/util'
import Constants from 'offline/constants'
import GamesFilter from 'offline/filter/games'
import DB from 'offline/db'

export default Segment.extend({
    name: 'disparities',
    priority: 1,
    default_: true,
    requires: [],
    insight: ['disparity'],
    detail: [],
    types: [Constants.TYPE_USER, Constants.TYPE_GROUP, Constants.TYPE_BUDDIES],
    methods: {
        build_metrics_from_diffs: function(diffs, strongT, epsilon) {
            strongT = strongT || 2.0
            epsilon = epsilon || 1.0

            var n = diffs.length

            var out = {
                avg: null,
                gap: null,
                consistency: null,
                disagreements: null,
                overunder: [0, 0, 0],
                dispersion: null,
                n: n
            }

            if (n === 0) return out

            var sum = 0.0
            var abs = []

            var close = 0
            var strong = 0

            var over = 0
            var under = 0
            var equal = 0

            for (var i = 0; i < diffs.length; i++) {
                var d = parseFloat(diffs[i])
                sum += d

                var a = Math.abs(d)
                abs.push(a)

                if (a <= epsilon) close++
                if (a >= strongT) strong++

                if (d > 0) over++
                else if (d < 0) under++
                else equal++
            }

            var mean = sum / n

            // Median(abs) as "typical gap"
            abs.sort(function(a, b) { return a - b })
            var mid = Math.floor(n / 2)
            var medianAbs = (n % 2 === 1) ? abs[mid] : ((abs[mid - 1] + abs[mid]) / 2.0)

            // Dispersion (population std dev) of signed diffs
            var variance = 0.0
            if (n > 1) {
                for (var vi = 0; vi < diffs.length; vi++) {
                    variance += (parseFloat(diffs[vi]) - mean) * (parseFloat(diffs[vi]) - mean)
                }
                variance /= n
            }

            out.avg = mean
            out.gap = medianAbs
            out.consistency = (close / n) * 100.0
            out.disagreements = (strong / n) * 100.0

            out.overunder = [
                (under / n) * 100.0,
                (equal / n) * 100.0,
                (over / n) * 100.0
            ]

            out.dispersion = (n > 1) ? Math.sqrt(variance) * 100 : 0.0

            return out
        }
    },
    run: function(type, owner, userIndex, detail, filter, vars) {

        if (!owner.get_count_ratings()) return {}

        var limit = detail ? 100 : 5

        var filterType = (filter.disparity !== undefined) ? filter.disparity : 'all'

        var searchFilter = {
            expansions: 0,
            sort: 'rating.gap',
            sortReverse: 1
        }

        if (type === Constants.TYPE_GROUP || type === Constants.TYPE_BUDDIES) {
            searchFilter.threshold = { group: owner.get_threshold() }
        }

        var metrics = {
            avg: null,
            gap: null,
            consistency: null,
            disagreements: null,
            overunder: null,
            dispersion: null,
            n: 0
        }

        var disparityGames = []
        var diffs = []

        // Fast path for filterType='all' in user context: skip the heavy
        // GamesFilter pipeline. The metrics only need (rating_value,
        // game_rating_avg) for every rated, non-expansion game — that's a
        // single SQL join. Then a small filter call materialises just the
        // top-N display rows. Verified parity-equivalent against prod
        // (offline metrics === prod metrics for the no-filter case).
        if (type === Constants.TYPE_USER && filterType === 'all') {

            var diffRows = DB.queryFast(
                'SELECT r.rating_value AS ur, g.game_rating_avg AS bgg' +
                ' FROM rating r JOIN game g ON g.game_id = r.game_id' +
                ' WHERE r.user_id = ' + owner.get_id() +
                ' AND r.rating_value > 0' +
                ' AND g.game_rating_avg > 0' +
                ' AND g.game_type = 1'
            )

            // Both user rating and BGG rating are rounded to 1 decimal in
            // the filter package phase (filter/package/games/game/package/{user,about}.js).
            // We must round here too or the metrics drift by 1-3 percentage
            // points around the consistency / disagreements thresholds.
            for (var dr = 0; dr < diffRows.length; dr++) {
                var dUser = parseFloat(diffRows[dr].ur)
                var dBgg = parseFloat(diffRows[dr].bgg)
                if (isNaN(dUser) || isNaN(dBgg)) continue
                dUser = Math.round(dUser * 10) / 10
                dBgg = Math.round(dBgg * 10) / 10
                diffs.push(dUser - dBgg)
            }

            var displaySearch = GamesFilter.forge({
                owner: owner,
                cache: false,
                filter: searchFilter,
                page: 1,
                perpage: limit,
                knownUsers: []
            }).run().to_array()

            disparityGames = (displaySearch.games || []).slice(0, limit)

            for (var gd = 0; gd < disparityGames.length; gd++) {
                if (disparityGames[gd] && disparityGames[gd].game && disparityGames[gd].game.id) {
                    this._references.games.push(parseInt(disparityGames[gd].game.id, 10))
                }
            }

            if (displaySearch.data && Array.isArray(displaySearch.data.users)) {
                for (var ud = 0; ud < displaySearch.data.users.length; ud++) {
                    if (displaySearch.data.users[ud] && displaySearch.data.users[ud].id) {
                        this._references.users.push(parseInt(displaySearch.data.users[ud].id, 10))
                    }
                }
            }

        } else {

            // Slow path: under/over filters and group/buddies contexts. The
            // filter has no built-in way to express the disparity sign, and
            // a partial top-N fetch isn't safe for those branches.
            var search = GamesFilter.forge({
                owner: owner,
                cache: false,
                filter: searchFilter,
                page: 1,
                perpage: owner.get_count_ratings ? owner.get_count_ratings() : limit,
                knownUsers: []
            }).run().to_array()

            var games = search.games || []
            var finalGames = []

            for (var i = 0; i < games.length; i++) {
                var game = games[i]

                if (game.group && game.group.rating_avg !== undefined && game.game && game.game.rating_avg !== undefined) {
                    var userAvg = parseFloat(game.group.rating_avg)
                    var bggAvg = parseFloat(game.game.rating_avg)
                    var diff = userAvg - bggAvg

                    if (filterType === 'all' || (filterType === 'under' && diff < 0) || (filterType === 'over' && diff > 0)) {
                        finalGames.push(game)
                    }

                    diffs.push(diff)
                }
            }

            disparityGames = finalGames.length > limit ? finalGames.slice(0, limit) : finalGames

            for (var gi = 0; gi < disparityGames.length; gi++) {
                if (disparityGames[gi] && disparityGames[gi].game && disparityGames[gi].game.id) {
                    this._references.games.push(parseInt(disparityGames[gi].game.id, 10))
                }
            }

            if (search.data && Array.isArray(search.data.users)) {
                for (var ui = 0; ui < search.data.users.length; ui++) {
                    if (search.data.users[ui] && search.data.users[ui].id) {
                        this._references.users.push(parseInt(search.data.users[ui].id, 10))
                    }
                }
            }

        }

        if (!detail) metrics = this.build_metrics_from_diffs(diffs, 2.0, 1.0)

        if (detail) {
            return {
                disparities: disparityGames
            }
        } else {
            return {
                disparities: {
                    games: disparityGames,
                    metrics: metrics
                }
            }
        }

    }
})
